export const ALL_SPORTS = [
  'AFL', 'AFLW', 'NRL', 'NRLW', 'RLWC',
  'MLS', 'BUNDESLIGA', 'UCL', 'EPL', 'LA_LIGA', 'SERIE_A', 'LIGUE_1', 'A_LEAGUE', 'WORLD_CUP',
  'NBA', 'WNBA', 'NBL', 'MLB', 'NFL', 'NHL',
  // MMA. One id per PROMOTION (UFC today); the two fighters ride in
  // home_team/away_team, ordered alphabetically rather than by corner.
  'UFC',
  // Tennis — ATP. One id per TOURNAMENT, not one per tour, because tournaments
  // overlap (Canada and Cincinnati both ran in Aug 2026) and a single 'tennis'
  // id could not serve both. Ids must match SPORTS_CONFIG in the public API's
  // public-api.py, which in turn matches TENNISSCRAPER/_SHARED/leagues.py.
  'ATP_AUSTRALIAN_OPEN', 'ATP_FRENCH_OPEN', 'ATP_WIMBLEDON', 'ATP_US_OPEN',
  'ATP_INDIAN_WELLS', 'ATP_MIAMI', 'ATP_MONTE_CARLO', 'ATP_MADRID', 'ATP_ROME',
  'ATP_CANADA', 'ATP_CINCINNATI', 'ATP_SHANGHAI', 'ATP_PARIS', 'ATP_FINALS',
  // The whole Challenger TIER behind one id, unlike the 14 above which are each
  // ONE tournament. ~190 events a year with ids minted per event, so they are
  // never listed individually — several arrive in a single response and the
  // event name rides on each game as `tournament`. See eventLabel() below.
  'ATP_CHALLENGER',
]

export const SPORT_DISPLAY_NAMES = {
  'NBA':         'NBA',
  'WNBA':        'WNBA',
  'NBL':         'NBL',
  'AFL':         'AFL',
  'AFLW':        'AFLW',
  'NRL':         'NRL',
  'NRLW':        'NRLW',
  'RLWC':        'RL World Cup',
  'MLS':         'MLS',
  'BUNDESLIGA':  'Bundesliga',
  'UCL':         'UCL',
  'EPL':         'EPL',
  'LA_LIGA':     'La Liga',
  'SERIE_A':     'Serie A',
  'LIGUE_1':     'Ligue 1',
  'A_LEAGUE':    'A-League',
  'WORLD_CUP':   'World Cup',
  'MLB':         'MLB',
  'NFL':         'NFL',
  'NHL':         'NHL',
  'UFC':         'UFC',
  'ATP_AUSTRALIAN_OPEN': 'ATP Australian Open',
  'ATP_FRENCH_OPEN':     'ATP French Open',
  'ATP_WIMBLEDON':       'ATP Wimbledon',
  'ATP_US_OPEN':         'ATP US Open',
  'ATP_INDIAN_WELLS':    'ATP Indian Wells',
  'ATP_MIAMI':           'ATP Miami',
  'ATP_MONTE_CARLO':     'ATP Monte Carlo',
  'ATP_MADRID':          'ATP Madrid',
  'ATP_ROME':            'ATP Rome',
  // Display stays generic: this tournament alternates host city each year
  // (Montreal 2026, Toronto 2027), and a city name here would need an annual edit.
  'ATP_CANADA':          'ATP Canada',
  'ATP_CINCINNATI':      'ATP Cincinnati',
  'ATP_SHANGHAI':        'ATP Shanghai',
  'ATP_PARIS':           'ATP Paris',
  'ATP_FINALS':          'ATP Finals',

  // A CATEGORY, not an event: one Challenger response carries several
  // tournaments at once. Rows show the game's own `tournament` instead wherever
  // the feed sent one, so this is the fallback. Use eventLabel() to get both.
  'ATP_CHALLENGER':      'ATP Challenger',

  // Retired ids, kept for DISPLAY ONLY. The API renamed the Slams to ATP_* in
  // Aug 2026, but bets tracked under the old ids are still in user_bets and
  // would otherwise render as a raw key. Deliberately absent from ALL_SPORTS and
  // SPORT_HIERARCHY so they cannot be selected or filtered on.
  'MENS_AO':        'Mens Australian Open',
  'MENS_RG':        'Mens Roland Garros',
  'MENS_WIMBLEDON': 'Mens Wimbledon',
  'MENS_USO':       'Mens US Open',
}

export const SPORT_HIERARCHY = {
  'Rugby League':      { leagues: ['NRL', 'NRLW', 'RLWC'] },
  'Aussie Rules':      { leagues: ['AFL', 'AFLW'] },
  'Soccer':            { leagues: ['MLS', 'BUNDESLIGA', 'UCL', 'EPL', 'LA_LIGA', 'SERIE_A', 'LIGUE_1', 'A_LEAGUE', 'WORLD_CUP'] },
  'Basketball':        { leagues: ['NBA', 'WNBA', 'NBL'] },
  'Baseball':          { leagues: ['MLB'] },
  'American Football': { leagues: ['NFL'] },
  'Hockey':            { leagues: ['NHL'] },
  'MMA':               { leagues: ['UFC'] },
  'Tennis':            { leagues: ['ATP_AUSTRALIAN_OPEN', 'ATP_FRENCH_OPEN', 'ATP_WIMBLEDON', 'ATP_US_OPEN', 'ATP_INDIAN_WELLS', 'ATP_MIAMI', 'ATP_MONTE_CARLO', 'ATP_MADRID', 'ATP_ROME', 'ATP_CANADA', 'ATP_CINCINNATI', 'ATP_SHANGHAI', 'ATP_PARIS', 'ATP_FINALS', 'ATP_CHALLENGER'] },
}

/**
 * Display label for an opportunity or game row.
 *
 * Multi-tournament sports (today only ATP_CHALLENGER) name a TIER in their
 * sport id, so every Challenger row would otherwise read identically. Those
 * carry the event on the row itself as `tournament` ("ATP Challenger 75 Como").
 * Every other sport has no such field and falls through to the display map
 * exactly as before.
 */
export const eventLabel = (row) =>
  row?.tournament || SPORT_DISPLAY_NAMES[row?.sport] || row?.sport || ''

/**
 * Short display label: the same as eventLabel(), minus the tier prefix that a
 * multi-tournament sport repeats on every single row.
 *
 * "ATP Challenger 75 Como" -> "75 Como". The Sport column already says which
 * tour this is, so those 14 characters carry no information and cost more
 * width than the whole column used to take. Pair it with the full name in a
 * `title` so nothing is actually lost.
 *
 * Only rows that HAVE a `tournament` are touched — every other sport returns
 * eventLabel() untouched, so no existing label can change. The tournament
 * string is the book's own listing text (challenger_discovery.py takes
 * `comp.name.strip()` verbatim, it is not normalised), so a name that does not
 * start with the expected prefix, or that is nothing BUT the prefix, falls
 * back to the full string rather than rendering empty.
 */
export const eventLabelShort = (row) => {
  if (!row?.tournament) return eventLabel(row)
  const short = row.tournament.replace(/^\s*(?:ATP\s+)?Challengers?\b\s*/i, '').trim()
  return short || row.tournament
}

/**
 * Split a multi-tournament label into its two display lines, or null when the
 * row should stay on one line.
 *
 * { tier: 'ATP Challenger', event: '75 Como' } lets a badge stack the tour
 * above the event, so the event reads without the tier being lost entirely —
 * the middle ground between eventLabel() (full, wide) and eventLabelShort()
 * (event only). Null means there is nothing to stack: any sport without a
 * `tournament`, and any tournament whose name did not carry the prefix (the
 * city-first "Cary Challenger"), where a tier line would only repeat itself.
 */
export const eventLabelParts = (row) => {
  const event = eventLabelShort(row)
  if (!row?.tournament || event === row.tournament) return null
  return { tier: SPORT_DISPLAY_NAMES[row.sport] || row.sport, event }
}
